import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { makeStyles, Typography, useTheme, Button } from '@material-ui/core';
import type { Place, RowBoundary, RackBoundary } from '../types/warehouse.types';
import { CloseButton } from './shared/CloseButton';
import { Row2DProjectionRenderer } from '../renderers/Row2DProjectionRenderer';
import { useRow2DProjectionDimensions } from '../hooks/useRow2DProjectionDimensions';
import { isMouseOverPlace2D } from '../utils/hitDetection';
import { getPlacesByRow } from '../utils/placeUtils';
interface Row2DProjectionCanvasProps {
  selectedRow: RowBoundary;
  places: Map<string, Place>;
  activeLevels: Set<number>;
  rackBoundaries: RackBoundary[];
  onPlaceSelect: (place: Place | null) => void;
  onPlaceMultiSelect?: (place: Place, ctrlKey: boolean) => void;
  onClose: () => void;
  onPlaceDoubleClick?: (place: Place) => void;
  onSelectAllRowPlaces: (row: RowBoundary) => void;
  canvasWidth: number;
  selectedPlaceIds: Set<string>;
  showSelectAll?: boolean;
}

const useStyles = makeStyles((theme) =>({
  headerContainer:{
    position: "absolute",
    top: 1,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px", 
    zIndex: 20,
  },

  headerText: {
    fontWeight: 600,
    fontSize: "1.25rem",
    color: theme.palette.primary.main,
    whiteSpace: "nowrap",
  },

  selectButton:{
    background: theme.palette.storageUi.backgroundSecondary,
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    textTransform: "uppercase",
    cursor: "pointer",
  },

  closeButton:{
    position: "absolute",
    top: 4,
    right: 12,
    color: theme.palette.secondary.main,
    cursor: "pointer",
    zIndex: 20,
  },

  canvas:{
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 10,
    pointerEvents: 'auto',
  },
}))

export const Row2DProjectionCanvas: React.FC<Row2DProjectionCanvasProps> = ({
  selectedRow,
  places,
  activeLevels,
  rackBoundaries,
  onPlaceSelect,
  onPlaceMultiSelect,
  onClose,
  onSelectAllRowPlaces,
  canvasWidth,
  onPlaceDoubleClick,
  selectedPlaceIds,
  showSelectAll = true
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const translate = useTranslate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<Row2DProjectionRenderer | null>(null);
  const [cursorStyle, setCursorStyle] = React.useState<'crosshair' | 'pointer'>('crosshair');

  useEffect(() => {
    rendererRef.current = new Row2DProjectionRenderer(theme);
  }, [theme]);

  // Calculate canvas dimensions using shared hook
  const { canvasHeight } = useRow2DProjectionDimensions(places, selectedRow, activeLevels, canvasWidth);

  useEffect(() => {
    const renderer = rendererRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (renderer && ctx && canvas) {
      // Clear the entire canvas before rendering to prevent old content from showing through
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      renderer.render(
        ctx,
        canvas.width,
        canvas.height,
        selectedRow,
        places,
        activeLevels,
        rackBoundaries,
        selectedPlaceIds
      );
    }
  }, [selectedRow, places, activeLevels, rackBoundaries, selectedPlaceIds]);

  // Memoize filtered places for this row to avoid recalculating on every mouse event
  const placesInRow = useMemo(() => {
    return getPlacesByRow(places,selectedRow.name, activeLevels)
  }, [places, selectedRow.name, activeLevels]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let isOverPlace = false;
    for (const place of placesInRow) {
      if (isMouseOverPlace2D(x, y, place)) {
        isOverPlace = true;
        break;
      }
    }

    setCursorStyle(isOverPlace ? 'pointer' : 'crosshair');
  }, [placesInRow]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let clickedPlace: Place | null = null;
    for (const place of placesInRow) {
      if (isMouseOverPlace2D(x, y, place)) {
        clickedPlace = place;
        break;
      }
    }

    // Handle multi-select with Ctrl/Cmd key
    if (clickedPlace) {
      if (e.ctrlKey || e.metaKey) {
        onPlaceMultiSelect?.(clickedPlace, true);
      } else {
        onPlaceSelect(clickedPlace);
      }
    }
  }, [placesInRow, onPlaceSelect, onPlaceMultiSelect]);
  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let clickedPlace: Place | null = null;
    for (const place of placesInRow) {
      if (isMouseOverPlace2D(x, y, place)) {
        clickedPlace = place;
        break;
      }
    }

    // Handle double-click to open modal
    if (clickedPlace && onPlaceDoubleClick) {
      onPlaceDoubleClick(clickedPlace);
    }
  }, [placesInRow, onPlaceDoubleClick]);

  const rowTitle = selectedRow.label
    ? `${selectedRow.name} • ${selectedRow.label}`
    : selectedRow.name

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.headerText}
        >{translate('osfc.row_2d_projection.title', { row_title: rowTitle })}</Typography>

        {showSelectAll && (
          <Button
            onClick={()=> onSelectAllRowPlaces(selectedRow)}
            variant='outlined'
            size='small'
            className={classes.selectButton}
          >
            {translate("osfc.buttons.select_all")}
          </Button>
        )}
      </div>
      
      <CloseButton onClick={onClose} className={classes.closeButton} />
      
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        className={classes.canvas}
        style={{cursor: cursorStyle}}
      />
    </>
  );
};
